import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ArrowDown2 } from "iconsax-reactjs";
import { X } from "lucide-react";
import { useMemo, useState } from "react";
import { AdminRecord } from "../../../shared/api/admins";
import { useAdmins } from "../../../shared/api/hooks/useAdmins";
import { useRoles } from "../../../shared/api/hooks/useRoles";
import { RoleAdmin } from "../../../shared/api/roles";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "../../../shared/ui";

type EscalationRecipient = {
  email: string;
  firstName: string;
};

type EscalationPayload = {
  recipients?: EscalationRecipient[];
  role?: string;
  roleName?: string;
  note?: string;
};

type KybComplianceEscalateModalProps = {
  open: boolean;
  isSubmitting?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: EscalationPayload) => void;
};

const getAdminName = (admin: AdminRecord | RoleAdmin) => {
  const roleAdminName = "name" in admin ? admin.name : "";
  const name =
    `${admin.firstName ?? ""} ${admin.lastName ?? ""}`.trim() ||
    roleAdminName ||
    admin.email.split("@")[0];

  return name || "Unknown member";
};

const getAdminFirstName = (admin: AdminRecord | RoleAdmin) =>
  admin.firstName?.trim() || getAdminName(admin).split(/\s+/)[0] || "Admin";

const KybComplianceEscalateModal = ({
  open,
  isSubmitting,
  onOpenChange,
  onSubmit,
}: KybComplianceEscalateModalProps) => {
  const [targetType, setTargetType] = useState<"role" | "member">("role");
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [note, setNote] = useState("");
  const { roles, isLoading: isLoadingRoles } = useRoles();
  const { admins, isLoading: isLoadingAdmins } = useAdmins({
    isProfileCompleted: true,
  });

  const selectedRole = useMemo(
    () => roles.find((role) => role.id === selectedRoleId) ?? null,
    [roles, selectedRoleId],
  );
  const selectedMember = useMemo(
    () => admins.find((admin) => admin._id === selectedMemberId) ?? null,
    [admins, selectedMemberId],
  );
  const memberRecipients = selectedMember
    ? [
        {
          email: selectedMember.email,
          firstName: getAdminFirstName(selectedMember),
        },
      ]
    : [];
  const canSubmit =
    (targetType === "role" ? Boolean(selectedRole) : memberRecipients.length > 0) &&
    !isSubmitting;

  const handleOpenChange = (nextOpen: boolean) => {
    if (isSubmitting) return;
    onOpenChange(nextOpen);
    if (!nextOpen) {
      setTargetType("role");
      setSelectedRoleId("");
      setSelectedMemberId("");
      setNote("");
    }
  };

  const dropdownLabel =
    targetType === "role"
      ? selectedRole?.name || (isLoadingRoles ? "Loading roles..." : "Select role")
      : selectedMember
        ? getAdminName(selectedMember)
        : isLoadingAdmins
          ? "Loading members..."
          : "Select member";

  return (
    <Modal
      open={open}
      onOpenChange={handleOpenChange}
      overlayClassName="bg-black/75"
      contentClassName="max-w-[600px] bg-white"
    >
      <ModalHeader className="items-center aku-heading-h2-semibold">
        <ModalTitle>Escalate to...</ModalTitle>
        <button
          type="button"
          aria-label="Close modal"
          disabled={isSubmitting}
          onClick={() => handleOpenChange(false)}
          className="rounded-full p-2 text-[#161827] hover:bg-[#F2F4FF] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <X size={20} />
        </button>
      </ModalHeader>

      <ModalBody className="pt-4">
        <div className="flex items-center gap-8">
          {(["role", "member"] as const).map((type) => {
            const isActive = targetType === type;

            return (
              <button
                key={type}
                type="button"
                disabled={isSubmitting}
                onClick={() => setTargetType(type)}
                className="inline-flex items-center gap-3 aku-body-normal-medium text-[#2C2F44] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span
                  className={`grid h-5 w-5 place-items-center rounded-full border ${
                    isActive ? "border-[#6F00FF]" : "border-[#C7CEE4]"
                  }`}
                >
                  {isActive ? (
                    <span className="h-3 w-3 rounded-full bg-[#6F00FF]" />
                  ) : null}
                </span>
                {type === "role" ? "Role" : "Member"}
              </button>
            );
          })}
        </div>

        <div className="mt-8">
          <div className="aku-body-small-medium text-[#2C2F44]">
            {targetType === "role" ? "Role" : "Member"}
          </div>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                disabled={isSubmitting}
                className="mt-2 flex h-14 w-full items-center justify-between rounded-[14px] bg-[#E9ECF8] px-5 text-left disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span className="truncate aku-body-normal-regular text-[#737AA8]">
                  {dropdownLabel}
                </span>
                <ArrowDown2 size={20} className="text-[#6F739B]" />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="start"
                sideOffset={8}
                className="z-[120] max-h-[260px] w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto rounded-[14px] border border-[#E1E6FF] bg-white p-2 shadow-[0_16px_36px_rgba(15,20,40,0.12)]"
              >
                {targetType === "role" ? (
                  roles.length ? (
                    roles.map((role) => (
                      <DropdownMenu.Item
                        key={role.id}
                        onSelect={() => setSelectedRoleId(role.id)}
                        className={`cursor-pointer rounded-[10px] px-3 py-2 aku-body-small-medium outline-none transition-colors focus:bg-[#F5F6FD] ${
                          role.id === selectedRoleId
                            ? "bg-[#F1ECFF] text-[#5E01D6]"
                            : "text-[#383B56]"
                        }`}
                      >
                        {role.name}
                      </DropdownMenu.Item>
                    ))
                  ) : (
                    <div className="px-3 py-2 aku-body-small-regular text-[#6F739B]">
                      {isLoadingRoles ? "Loading roles..." : "No roles found."}
                    </div>
                  )
                ) : admins.length ? (
                  admins.map((admin) => (
                    <DropdownMenu.Item
                      key={admin._id}
                      onSelect={() => setSelectedMemberId(admin._id)}
                      className={`cursor-pointer rounded-[10px] px-3 py-2 outline-none transition-colors focus:bg-[#F5F6FD] ${
                        admin._id === selectedMemberId
                          ? "bg-[#F1ECFF] text-[#5E01D6]"
                          : "text-[#383B56]"
                      }`}
                    >
                      <div className="aku-body-small-medium">
                        {getAdminName(admin)}
                      </div>
                      <div className="aku-body-small-regular text-[#6F739B]">
                        {admin.email}
                      </div>
                    </DropdownMenu.Item>
                  ))
                ) : (
                  <div className="px-3 py-2 aku-body-small-regular text-[#6F739B]">
                    {isLoadingAdmins ? "Loading members..." : "No members found."}
                  </div>
                )}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>

        <div className="mt-8">
          <div className="aku-body-small-medium text-[#2C2F44]">
            Note <span className="aku-body-small-regular italic">• Optional</span>
          </div>
          <textarea
            value={note}
            disabled={isSubmitting}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Add notes"
            className="mt-2 min-h-[120px] w-full resize-none rounded-[14px] bg-[#E9ECF8] px-4 py-3 text-[14px] font-[350] text-gray-30 placeholder:text-gray-20 focus:outline-none focus:ring-2 focus:ring-[#D9DEFF] disabled:cursor-not-allowed disabled:opacity-60"
          />
        </div>
      </ModalBody>

      <ModalFooter>
        <Button
          type="button"
          disabled={!canSubmit}
          onClick={() =>
            onSubmit(
              targetType === "role" && selectedRole
                ? {
                    role: selectedRole.id,
                    roleName: selectedRole.name,
                    note,
                  }
                : { recipients: memberRecipients, note },
            )
          }
          className="h-11 rounded-[14px] px-6 text-[16px] font-[500]"
        >
          {isSubmitting ? "Escalating..." : "Escalate"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default KybComplianceEscalateModal;
